import { Access } from '../../../../shared/typings/user';
import { WabaIntegration, WabaIntegrationMemberStatus } from '../../../../shared/typings/waba';
import { WabaIntegrationAssignee } from '../../../../shared/typings/waba';
import { Message, REMINDER_MESSAGE_STATUS, ScheduledMessageStatus } from './message-types';
import { TemplateBodyComponent, TemplateButtonComponent } from '../../../../shared/typings/template';
import { PaginationDirection } from '../requests/get-paginated-chat';
import { AgentStatus } from '../../../agent-availability/typings';
import { ScheduledMessage } from '../requests/get-scheduled-messages';
import { CustomFieldCondition } from '../components/chat-list-section/custom-field-filter/typings';

export interface Address {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  countryCode?: string;
  type?: string;
}

interface ContactEmail {
  email?: string;
  type?: string;
}

interface ContactPhone {
  phone?: string;
  type?: string;
  isOnWhatsApp?: boolean;
}

interface ContactUrls {
  url?: string;
  type?: string;
}

interface ContactName {
  fullName: string;
  firstName: string;
  middleName?: string;
  lastName?: string;
  suffix?: string;
  prefix?: string;
}

interface ContactOrganisation {
  company?: string;
  department?: string;
  title?: string;
}

export interface ContactData {
  addresses?: Array<Address>;
  birthday?: string;
  emails?: Array<ContactEmail>;
  name: ContactName;
  org?: ContactOrganisation;
  phones?: Array<ContactPhone>;
  urls?: Array<ContactUrls>;
}

export type ChatAnalytics = {
  chatMessageId: string;
  chatId: string;
  orgId: string;
  sentCount: number;
  deliveryCount: number;
  readCount: number;
  repliesCount: number;
  unreadCount: number;
  noResponseCount: number;
};

export interface TeamMemberBasicInfo {
  id: string;
  name: string;
  provider?: string;
  phone?: string | null;
  status?: AgentStatus | null;
}

export interface TeamMember extends TeamMemberBasicInfo {
  phone: string;
  role: string;
  inviteId?: string;
  invitedOn?: number;
  status: AgentStatus | null;
  statusExpiration: string | null;
  orgRoleId?: string;
  orgRoleName?: string;
  email?: string;
  assignedIntegrationsCount?: number;
  primaryIntegrationId?: string;
  primaryIntegrationName?: string;
  assignments?: WabaIntegrationAssignee[];
  roleName?: string;
}

export interface GetTeamMembers {
  teamMembers: TeamMember[];
  name: string;
  isDefault: boolean;
}

export interface GetUploadCredentials {
  url: string;
  expiresIn: number;
}

export interface UploadMedias {
  key: string;
  contentType: string;
  chatId?: string;
}

export enum UploadMediasType {
  CHAT_MESSAGES = 'CHAT_MESSAGES',
  QUICK_REPLY = 'QUICK_REPLY',
}

export enum ChatWindowSourcePage {
  BROADCAST = 'BROADCAST',
  CHAT = 'CHAT',
  CUSTOMERS = 'CUSTOMERS',
  CALLS = 'CALLS',
  WA_GROUPS = 'WA_GROUPS',
}

export enum SendMessageType {
  TEXT = 'text',
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  DOCUMENT = 'document',
  CONTACT = 'contact',
}

export enum AttachmentTypes {
  MEDIA = 'media',
  AUDIO = 'audio',
  DOCUMENT = 'document',
  PDF = 'pdf',
  TEMPLATE = 'template',
  PRODUCT = 'product',
  CATALOGUE = 'catalogue',
}

export enum CommerceTypes {
  PRODUCT = 'product',
  CATALOGUE = 'catalogue',
}

export enum ChatListReducerActionTypes {
  SEARCH = 'searchTerm',
  FORWARD_SEARCH = 'forwardSearchTerm',
  SEARCH_STATUS = 'isSearchActive',
  CONVERSATION_VIEW = 'conversationView',
  STATUS = 'status',
  TAGS = 'tags',
  WABA = 'waba',
  SLA_STATUS = 'slaStatus',
  ASSIGNED_USER = 'assignedUserId',
  RESET = 'reset',
  CUSTOM_LIST = 'customList',
  CURRENT_CUSTOM_LIST_ID = 'currentCustomListId',
  LEAD_CUSTOM_FIELD_LIST = 'leadCustomFieldIdList',
  MIXED = 'mixed',
  TAG_IDS = 'tagIds',
  CUSTOM_FIELD_CONDITIONS = 'customFieldConditions',
}

export type ChatFilterSlaStatus = 'breached' | 'non-breached';
export type ConversationView = 'chats' | 'groups';

export enum EntityTypes {
  CUSTOMER = 'CUSTOMER',
  CHAT = 'CHAT',
}

export interface ChatListAtom {
  [ChatListReducerActionTypes.SEARCH]: string;
  [ChatListReducerActionTypes.FORWARD_SEARCH]?: string;
  [ChatListReducerActionTypes.SEARCH_STATUS]: boolean;
  [ChatListReducerActionTypes.CONVERSATION_VIEW]: ConversationView;
  [ChatListReducerActionTypes.STATUS]: ChatStatuses;
  [ChatListReducerActionTypes.TAGS]: string[];
  [ChatListReducerActionTypes.TAG_IDS]: string[];
  [ChatListReducerActionTypes.WABA]: string[];
  [ChatListReducerActionTypes.SLA_STATUS]: ChatFilterSlaStatus;
  [ChatListReducerActionTypes.ASSIGNED_USER]: string;
  [ChatListReducerActionTypes.CURRENT_CUSTOM_LIST_ID]: string;
  [ChatListReducerActionTypes.LEAD_CUSTOM_FIELD_LIST]?: {
    listId: string;
    fieldId: string;
  };
  [ChatListReducerActionTypes.CUSTOM_LIST]: {
    [key: string]: string;
  };
  [ChatListReducerActionTypes.CUSTOM_FIELD_CONDITIONS]: CustomFieldCondition[];
}

export enum SendMessageTimeType {
  NOW = 'now',
  SCHEDULE = 'schedule',
  NONE = 'none',
}

export enum ChatStatuses {
  ALL = 'all',
  OPEN = 'open',
  CLOSED = 'closed',
  READ = 'read',
  UNREAD = 'unread',
  WAITING_FOR_RESPONSE = 'waiting_for_response',
  DONE = 'done',
  UNRESOLVED = 'unresolved',
}

export const ChatStatusOptions = [
  {
    name: 'All',
    value: ChatStatuses.ALL,
  },
  {
    name: 'Unread',
    value: ChatStatuses.UNREAD,
  },
  {
    name: 'Read',
    value: ChatStatuses.READ,
  },
  {
    name: 'Expired',
    value: ChatStatuses.CLOSED,
  },
  {
    name: 'Open',
    value: ChatStatuses.OPEN,
  },
  {
    name: 'Awaiting reply',
    value: ChatStatuses.WAITING_FOR_RESPONSE,
  },
  {
    name: 'Resolved',
    value: ChatStatuses.DONE,
  },
];

export const ChatStatusFilters = [
  {
    name: 'All',
    value: ChatStatuses.ALL,
  },
  {
    name: 'Unread',
    value: ChatStatuses.UNREAD,
  },
  {
    name: 'Read',
    value: ChatStatuses.READ,
  },
  {
    name: 'Expired',
    value: ChatStatuses.CLOSED,
  },
  {
    name: 'Open',
    value: ChatStatuses.UNRESOLVED,
  },
  {
    name: 'Awaiting reply',
    value: ChatStatuses.WAITING_FOR_RESPONSE,
  },
  {
    name: 'Closed',
    value: ChatStatuses.DONE,
  },
];

export const DEFAULT_CHAT_FILTERS = {
  searchTerm: '',
  isSearchActive: false,
  conversationView: 'chats' as ConversationView,
  assignedUserId: '',
  tags: [] as string[],
  status: ChatStatuses.ALL,
  customList: {} as { [key: string]: string },
  currentCustomListId: '',
  waba: [] as string[],
  slaStatus: 'non-breached' as ChatFilterSlaStatus,
  tagIds: [] as string[],
  customFieldConditions: [] as CustomFieldCondition[],
};

export type ChatListReducerAction =
  | SearchActive
  | StatusAction
  | SearchTerm
  | Tags
  | AssignUser
  | CurrentCustomList
  | SelectListField
  | Waba
  | SlaStatus
  | Reset
  | Mixed
  | LeadCustomFieldList
  | ForwardSearchTerm
  | TagIds
  | CustomFieldConditions;

interface CustomFieldConditions {
  type: ChatListReducerActionTypes.CUSTOM_FIELD_CONDITIONS;
  data: CustomFieldCondition[];
}

interface SearchActive {
  type: ChatListReducerActionTypes.SEARCH_STATUS;
  data: boolean;
}
interface StatusAction {
  type: ChatListReducerActionTypes.STATUS;
  data: ChatStatuses;
}

interface SearchTerm {
  type: ChatListReducerActionTypes.SEARCH;
  data: string;
}
interface ForwardSearchTerm {
  type: ChatListReducerActionTypes.FORWARD_SEARCH;
  data: string;
}
interface Tags {
  type: ChatListReducerActionTypes.TAGS;
  data: string | string[];
}

interface TagIds {
  type: ChatListReducerActionTypes.TAG_IDS;
  data: string | string[];
}

interface Waba {
  type: ChatListReducerActionTypes.WABA;
  data: string | string[];
}

interface SlaStatus {
  type: ChatListReducerActionTypes.SLA_STATUS;
  data: ChatFilterSlaStatus;
}

interface AssignUser {
  type: ChatListReducerActionTypes.ASSIGNED_USER;
  data: string;
}

interface CurrentCustomList {
  type: ChatListReducerActionTypes.CURRENT_CUSTOM_LIST_ID;
  data: string;
}
interface LeadCustomFieldList {
  type: ChatListReducerActionTypes.LEAD_CUSTOM_FIELD_LIST;
  data: { listId: string; fieldId: string };
}
interface SelectListField {
  type: ChatListReducerActionTypes.CUSTOM_LIST;
  data: {
    listId: string;
    fieldId: string;
  };
}

interface Reset {
  type: ChatListReducerActionTypes.RESET;
  data: undefined;
}

interface Mixed {
  type: ChatListReducerActionTypes.MIXED;
  data: Partial<typeof DEFAULT_CHAT_FILTERS>;
}

export interface InternalNote {
  id: string;
  note: string;
  createdBy: string;
  updatedBy: string;
  dateCreated: string;
  dateUpdated: string;
}

export interface InternalNotesListPaginationData {
  lastNoteId?: string;
  lastNoteDate?: string;
  hasMore?: boolean;
  totalCount: number;
}

export interface InternalNotesListResponse extends InternalNotesListPaginationData {
  notes: InternalNote[];
}

export interface CreateInternalNoteRequest extends Partial<InternalNote> {
  chatId: string;
  entities: NoteEntity[];
}

export interface ChatListPaginationData {
  lastTimestamp?: string;
}

export interface CustomerChatMeta {
  customerName: string;
  customerId: string;
  customerChatId: string;
  assignedUserId: string;
  optIn: boolean | null;
  assignedUserName: string;
  internalFields: CustomChatMetaFields[];
  customFields: CustomChatMetaFields[];
  tags: ConversationTag[];
  integrationId: string;
  integrationWabaNumber: string;
  integrationWabaPhoneName: string;
  integrationDisplayName: string;
  customerIsBlocked: boolean | null;
  customerPhone: string;
  isDone?: boolean;
  phonebookName?: string;
  imageUrl: string;
  wabaProfilePictureUrl?: string;
  scheduledChatMessagesCount?: number;
}

export interface CustomChatMetaSingleSelectField {
  id: string;
  name: string;
  type: 'single_select';
  value: string;
  dateUpdated: string;
  customFieldValueId: string;
  parentCustomFieldId?: string;
  haveBeenAddedAsChild?: boolean;
  entityType?: EntityTypes;
  visibility?: CustomFieldVisibility;
  isVisible?: boolean;
}

export interface CustomChatMetaMultiSelectField {
  id: string;
  name: string;
  type: 'multi_select';
  aiManaged?: boolean;
  description?: string;
  dateUpdated?: string;
  values: Array<{
    customFieldValueId: string;
    value: string;
    dateCreated?: string;
    dateUpdated?: string;
  }>;
  list?: SingleSelectCustomField[];
  entityType?: EntityTypes;
  visibility?: CustomFieldVisibility;
  isVisible?: boolean;
}

export interface CustomChatMetaTextField {
  id: string;
  name: string;
  value: string;
  dateUpdated: string;
  type: 'text' | 'date' | 'number' | 'boolean';
  aiManaged?: boolean;
  entityType?: EntityTypes;
  visibility?: CustomFieldVisibility;
  isVisible?: boolean;
}

export type CustomChatMetaFields =
  | CustomChatMetaTextField
  | CustomChatMetaSingleSelectField
  | CustomChatMetaMultiSelectField;

export type CustomChatMetaFieldsWithoutMultiSelect = CustomChatMetaTextField | CustomChatMetaSingleSelectField;

export interface CustomerDetails {
  id: string;
  dateCreated: Date;
  dateUpdated: Date;
  name: string;
  phoneNumber: string;
  source: string;
  orgId: string;
}

export interface CustomFieldsData {
  customFields: CustomFields[] | CustomFieldsWithEntityType[];
  internalFields: CustomFields[];
}

export type CustomFields = GetPrimitiveCustomField | GetSingleSelectCustomField | GetMultiSelectCustomField;
export type CustomFieldsWithEntityType = CustomFields & { entityType: EntityTypes };

export type CustomFieldsDetailsRequest = {
  name: string;
  type: CustomFieldsTypes;
  list?: { name: string }[];
};

export type CustomFieldsDetails = {
  id: string;
  name: string;
  uniqueName: string;
} & (
  | {
      list?: never;
      type: CustomFieldsTypes.DATE | CustomFieldsTypes.NUMBER | CustomFieldsTypes.TEXT | CustomFieldsTypes.BOOLEAN;
    }
  | {
      type: CustomFieldsTypes.LIST;
      list: Array<{
        id: string;
        name: string;
        position: number;
        visibility: boolean;
      }>;
    }
);
export interface GetAllCustomFields<T> {
  customFields: T[];
  internalFields: T[];
}

export interface SingleSelectCustomField {
  customerCount: number;
  id: string;
  name: string;
  position: number;
}

export interface NestedField {
  aiManaged: boolean;
  description: string | null;
  id: string;
  name: string;
  parentCustomFieldId: string;
  type: string;
  uniqueName: string;
}

export interface ChildList {
  customerCount: number;
  description: string | null;
  id: string;
  name: string;
  customFieldId: string;
  type: string;
  parentCustomFieldValueId: string;
  position: number;
}

export interface ParentChildList {
  customFieldId: string;
  description: string | null;
  id: string;
  name: string;
  parentCustomFieldValueId: string;
  position: number;
  uniqueName: string;
  childList: ChildList[];
}
export interface GetSingleSelectCustomField extends Omit<CustomChatMetaSingleSelectField, 'customFieldValueId'> {
  uniqueName: string;
  nestedFields: NestedField[] | null;
  lastFetchedUpdateDate: string;
  list: SingleSelectCustomField[];
  parentChildList: ParentChildList[];
  parentCustomFieldId?: string;
  aiManaged?: boolean;
}

export interface GetMultiSelectCustomField extends Omit<CustomChatMetaMultiSelectField, 'values'> {
  lastFetchedUpdateDate: string;
  uniqueName: string;
  list: SingleSelectCustomField[];
  values?: Array<{
    customFieldValueId: string;
    value: string;
  }>;
}

export interface GetPrimitiveCustomField extends CustomChatMetaTextField {
  uniqueName: string;
}

export interface LeadStatusListProps {
  customerCount: number;
  name: string;
  id: string;
  position: number;
}
export interface GetAllCustomFieldsLeadStatusProps {
  customField: {
    id: string;
    name: string;
    list: LeadStatusListProps[];
    type: string;
    uniqueName: string;
  };
}
export interface ConversationTag {
  id: string;
  name: string;
  bgColorCode: string;
  fontColorCode: string;
  createdOn?: string;
}

export interface GetConversationTagsPaginationData {
  hasMore: boolean;
  nextSearchAfter: string | null;
}

export interface GetConversationTagsData extends GetConversationTagsPaginationData {
  data: ConversationTag[];
}

export interface ConversationTagsData extends GetConversationTagsPaginationData {
  tags: ConversationTag[];
}

export interface GroupChatMeta {
  groupId: string;
  groupChatName: string;
  groupChatId: string;
  memberCount?: number;
  showMemberCount?: boolean;
}

export enum EligibleMessageTypes {
  'TEMPLATE' = 'TEMPLATE',
  'TEXT' = 'TEXT',
  'ANY' = 'ANY',
  // Only On UI Side
  'NONE' = 'NONE',
  'FRICTIONLESS' = 'FRICTIONLESS',
}

export type EligibleSubType = 'text' | 'image' | 'video' | 'audio' | 'pdf';

export type EligibleChatTypeInfoMessage = {
  text: string;
};
export enum TemplateComponentTypesKeys {
  SINGLE_MESSAGE = 'SINGLE_MESSAGE',
  SINGLE_MESSAGE_2 = 'SINGLE_MESSAGE_2',
  SINGLE_MESSAGE_3 = 'SINGLE_MESSAGE_3',
}

export type TemplateComponents = {
  [key in TemplateComponentTypesKeys]: [TemplateBodyComponent, TemplateButtonComponent];
};

export interface SendableMessageTemplateComponentTypes {
  type: EligibleMessageTypes;
  templateComponents: TemplateComponents | undefined;
  text: string;
  subTypes?: EligibleSubType[];
}
export interface GetPaginatedChatReturn {
  messages: Message[];
  canWrite: boolean;
  canRead: boolean;
  searchChatMessageId?: string;
  lastChatMessageId?: string;
  olderThanTimestamp?: string;
  latestChatMessageId?: string;
  newerThanTimestamp?: string;
  upcomingScheduledChatMessageTime?: number;
  upcomingScheduledChatMessageTimezone?: string | null;
}

export interface ChatPaginationData {
  searchChatMessageId?: string;
  searchChatMessageTime?: string;
  selectedDate?: string;
  scrollDirection?: PaginationDirection;
  lastChatMessageId?: string;
  olderThanTimestamp?: string;
  latestChatMessageId?: string;
  newerThanTimestamp?: string;
  hasMore?: boolean;
  hasMoreOlder?: boolean;
  hasMoreNewer?: boolean;
}

export interface DirectSendFormValues {
  phone: string;
  selectedCountryId: string;
}

export enum UserProvider {
  SUPPORT_AGENT = 'support_agent',
}

export interface UserDetails {
  id: string;
  dateCreated: Date;
  dateUpdated: Date;
  name: string | null;
  email: string;
  phone: string;
  countryCode: string;
  orgId: string;
  state?: any;
  city?: any;
  timezone?: any;
  currencyCode?: any;
  provider: UserProvider | string;
  isOrgOwner: boolean;
  ctwaAdsInsights: boolean;
  primaryIntegration: WabaIntegration | null;
  access: Access;
  closedConversationTemplateViewTypes: {
    type: FrictionlessTemplateTypes;
  }[];
  status?: AgentStatus;
  hasCallingLicense?: boolean;
}

export enum GroupLevelAccess {
  FULL_ACCESS = 'FULL_ACCESS',
  SEND_ONLY_ACCESS = 'SEND_ONLY_ACCESS',
  NO_ACCESS = 'NO_ACCESS',
}

export interface ChannelAccessUpdate {
  groupId: string;
  userId: string;
  accessLevel: GroupLevelAccess;
  permissionId: string;
  orgId?: string;
}

export enum AssigneeUpdatesType {
  UNASSIGNED = 'UNASSIGNED',
  ASSIGNED = 'ASSIGNED',
}

export interface AssignedSocketUpdates {
  chatId: string;
  chatType: string;
  chatTypeId: string;
  userId: string;
  type: AssigneeUpdatesType.UNASSIGNED | AssigneeUpdatesType.ASSIGNED;
  integrationId: string;
  validUserIds?: string[];
  assigneeUpdatedTime?: number; // from socket
  notificationTime?: string; // from FCM notification
}

export enum CustomFieldsTypes {
  TEXT = 'text',
  DATE = 'date',
  NUMBER = 'number',
  LIST = 'single_select',
  BOOLEAN = 'boolean',
  MULTI_SELECT = 'multi_select',
}

export enum CustomFieldVisibility {
  ALWAYS = 'ALWAYS',
  VALUESET = 'VALUESET',
  NEVER = 'NEVER',
}

export enum ChatsUpdatesTypes {
  DELETED = 'DELETED',
}

export interface ChatsUpdates {
  chatIds: string[];
  orgId: string;
  type: ChatsUpdatesTypes;
}

export enum OperationStatus {
  QUEUED = 'QUEUED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETE = 'COMPLETE',
}

export interface Sheet {
  name: string;
  totalRecords: number;
  successfulRecords: number;
  failedRecords: number;
}

export interface AdditionalData {
  fileName: string;
  totalRecords: number;
  successfulRecords: number;
  failedRecords: number;
  sheets: Sheet[];
  failedCsvLink: string | null;
}

export interface ImportExcelSocket {
  operationId: string;
  orgId: string;
  status: OperationStatus;
  type: string;
  additionalData: AdditionalData;
}

export interface AgentStatusSocket {
  userId: string;
  teamId: string;
  memberId: string;
  status: WabaIntegrationMemberStatus | null;
  statusExpiration: string | null;
}

export interface ErrorMessageData {
  reasons: string[];
  infoUrl: string;
}

export interface ErrorMessageResponseData extends ErrorMessageData {
  message: string;
}

export interface ErrorMessageResponse {
  errorMessages: ErrorMessageResponseData[];
}

export enum FrictionlessTemplateTypes {
  IMAGE = 'IMAGE_MESSAGE',
  VIDEO = 'VIDEO_MESSAGE',
  AUDIO = 'AUDIO_MESSAGE',
  PDF = 'PDF_MESSAGE',
  SINGLE_MESSAGE = 'SINGLE_MESSAGE',
}

export interface FrictionlessTemplateData {
  type: FrictionlessTemplateTypes;
  name: string;
  templateId: number;
  language: string;
}

export interface GetFrictionlessTemplatesResponse {
  templates: FrictionlessTemplateData[];
}

export interface FrictionlessMessageDataToSend {
  type: FrictionlessTemplateTypes;
  // name: string;
  // templateId: number;
  // language: string;
  fileName: string;
  link: string;
  text: string;
}

export enum ENTITY_TYPES {
  USER = 'USER',
  TEAM = 'TEAM',
}

export interface NoteEntity {
  value: string;
  entityId: string;
  entityType: ENTITY_TYPES;
}

export interface UpdateNoteMessageEventPayload {
  chat: {
    assignedUserId: string;
    assignedUserName: string;
    chatId: string;
    chatType: string;
    chatTypeId: string;
    imageUrl: string | null;
    integrationDisplayName: string;
    integrationId: string;
    integrationWabaNumber: string;
    integrationWabaPhoneName: string;
    isDone: boolean;
    name: string;
    optIn: boolean;
    phoneNumber: string;
    senderUser: {
      id: string;
      imageUrl: string | null;
      name: string;
      phoneNumber: string;
    };
    unreadCount: number;
  };
  chatId: string;
  integrationDisplayName: string;
  integrationId: string;
  integrationWabaNumber: string;
  integrationWabaPhoneName: string;
  isFromAd: boolean;
  latestIntegration: {
    chatId: string;
    integrationDisplayName: string;
    integrationId: string;
    integrationWabaNumber: string;
  };
  message: {
    messageType: string;
    text: string;
  };
  messageId: string;
  messageOriginType: string;
  messageTime: number;
  orgId: string;
  senderId: string;
  senderType: string;
  status: string;
  statusTimestamp: number;
  validUsersCustomerName: {
    name: string;
    userId: string;
  }[];
}

export interface GetDefaultTeamMembersResponse {
  teamMembers: TeamMemberBasicInfo[];
  deletedTeamMembers: TeamMemberBasicInfo[];
}

export interface TeamMembersPrivateNotes {
  allTeamMembers: TeamMemberBasicInfo[];
  activeTeamMembers: TeamMemberBasicInfo[];
}

export enum RecordingState {
  DEFAULT = 'DEFAULT',
  RECORDING = 'RECORDING',
  PAUSED = 'PAUSED',
  PLAYING = 'PLAYING',
}

export enum RealtimeSearchCountResolution {
  LOW = 'LOW',
  HIGH = 'HIGH',
}

export interface GetChatRealtimeCountStatusResponse {
  status: boolean;
  liveChatCountsResolution: RealtimeSearchCountResolution | null;
  liveChatCountsRefreshInterval: number | null;
}

export interface AIFeaturePermissionResponse {
  permissions: {
    rephrase_text: boolean;
    await_replies: boolean;
    chat_summary: boolean;
    call_summarization: boolean;
    custom_fields: boolean;
    generate_reply: boolean;
    chat_ai_awaiting_response: boolean;
    wa_group: boolean;
    agent_availability: boolean;
    ai_whatsapp_call: boolean;
    pstn_calling: boolean;
    ai_pstn_call: boolean;
    ai_campaigns: boolean;
    dynamics_365: boolean;
    schedule_chat_message: boolean;
  };
  pollingInterval: {
    reminders: number;
    chatMessages?: number;
  };
  pollingOrgIds?: string[] | null;
}

export interface GenerateReplyResponse {
  generatedReply: {
    text: string;
    messageType: string;
  } | null;
}

export interface AiBotType {
  id: string;
  orgId: string;
  name: string;
  faqCount: number;
  intentCount: number;
  isDefault: boolean;
  unresolvedIntentCount: number;
  documentCount: number;
  websiteCount: number;
  integrationIds: Array<string>;
  primaryIntegrationId: string | null;
  dateCreated: Date;
  dateUpdated: Date;
  dateDeleted: Date | null;
}

export interface ReminderCreatedPayload {
  id: string;
  chatMessageId: string;
  chatId: string;
  reminderId: string;
  reminderStatus: REMINDER_MESSAGE_STATUS;
  reminderMessage: string;
  assigneeDetails: { userId: string; name: string }[];
  dueAt: string;
  createdBy: string;
}

export interface ReminderTriggeredPayload {
  reminderId: string;
  chatId: string;
  reminderMessage: string;
  dueAt: string;
  assigneeDetails: { userId: string; name: string }[];
  chatMessageId: string;
  createdBy: string;
  createdByName: string;
  phoneNumber: string;
  wabaNumber: string;
  chat: {
    name: string;
    chatTypeId: string;
  };
  reminderTriggerMessageId: string;
}

export interface ReminderStatusUpdatePayload {
  chatId: string;
  isReminderTriggered: boolean;
  reminderId: string;
  reminderMessage: string;
  dueAt: string | null;
  assignees: string[];
  assigneeDetails: Array<{ userId: string; name: string | null }>;
  createdBy: string | null;
  createdByName: string | null;
  reminderStatus: REMINDER_MESSAGE_STATUS;
  reminderMessageId: string | null;
  reminderTriggerMessageId: string | null;
}

export interface GetCallConfigurationResponse {
  callSummarizationEnabled: boolean;
  callTranscriptionEnabled: boolean;
}

export interface ScheduledChatMessageSocketPayload {
  orgId: string;
  chatId: string;
  chatMessageId: string;
  chatType: string;
  event: 'CREATED' | 'UPDATED' | 'DELETED';
  scheduledTime: string;
  dateUpdated?: string;
  upcomingScheduledChatMessageTimezone: string;
  failedReason?: string | null;
  upcomingScheduledChatMessageTime: string | null;
  status: ScheduledMessageStatus;
  message: ScheduledMessage['message'];
  createdBy?: { userId: string; name: string };
}
